import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/ecommerce-web-design-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/ecommerce-web-design-colorado")({
  head: () => ({
    meta: [
      { title: "Ecommerce Web Design in Colorado: Costs, Platforms & What Matters (2026)" },
      { name: "description", content: "Ecommerce web design in Colorado: honest costs ($1,500 + $30/product as an add-on), platform choices, and what actually makes an online store sell." },
      { property: "og:title", content: "Ecommerce Web Design in Colorado: Costs, Platforms & What Matters" },
      { property: "og:description", content: "Honest ecommerce web design costs in Colorado, platform choices, and what makes an online store sell." },
      { property: "og:url", content: `${SITE_URL}/learn/ecommerce-web-design-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Ecommerce web design in Colorado",
        description: ARTICLE?.desc ?? "What ecommerce web design costs and what makes a store sell.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/ecommerce-web-design-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Web Design"
      read="9 min read"
      title="Ecommerce web design in Colorado: costs, platforms, and what actually matters"
      intro="Adding a store to your website shouldn't cost $30,000. Here's what ecommerce web design really costs in Colorado, how to pick a platform, and the things that actually decide whether your store sells or sits there."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Ecommerce web design" },
      ]}
      path="/learn/ecommerce-web-design-colorado"
      faqs={[
        { q: "How much does ecommerce web design cost in Colorado?", a: "As an add-on to a business website, ecommerce runs about $1,500 plus $30 per product on top of the base site. Standalone custom stores from big agencies can run $15,000–$50,000 — more than most small Colorado businesses need." },
        { q: "Which ecommerce platform is best for a small business?", a: "For most small Colorado businesses, a simple store built into your existing website beats a complex platform. You want easy product management, secure checkout, and fast pages — not enterprise features you'll never use." },
        { q: "Can I add ecommerce to my existing website?", a: "Usually yes. A store can be added to most modern sites without a full rebuild. The key questions are how many products you have and whether your current site is fast enough to support shopping traffic." },
      ]}
    >
      <h2>What ecommerce web design actually costs</h2>
      <p>
        Our published pricing is a fair benchmark: ecommerce is a <strong>$1,500 add-on plus
        $30 per product</strong> on top of your website package (which start at $1,850). So a
        20-product store on a starter site runs about $3,950 total — not the five figures many
        agencies quote. See the full breakdown on our <Link to="/pricing">pricing page</Link>.
      </p>

      <h2>Platform choice, in plain English</h2>
      <ul>
        <li><strong>Built-in store (our recommendation for most).</strong> Your products live on your own fast website. You own everything, no monthly platform tax, and your store benefits from your site's SEO.</li>
        <li><strong>Shopify and similar.</strong> Good for large catalogs and complex shipping, but you pay monthly fees forever and your store lives on someone else's platform.</li>
        <li><strong>Marketplaces (Etsy, Amazon).</strong> Fine as a side channel, but you build their business, not yours. Your own site should be home base.</li>
      </ul>

      <h2>What actually makes a store sell</h2>
      <ul>
        <li><strong>Speed.</strong> Every second of load time costs you buyers. A fast store beats a fancy one.</li>
        <li><strong>Real product photos and descriptions.</strong> Manufacturer copy pasted in ranks nowhere and convinces nobody.</li>
        <li><strong>A checkout with no surprises.</strong> Unexpected shipping costs at the last step is where most abandoned carts die.</li>
        <li><strong>Being found.</strong> A store nobody finds sells nothing. Product pages need the same SEO care as service pages — see how we handle <Link to="/colorado-seo">SEO across Colorado</Link>.</li>
      </ul>

      <h2>When ecommerce is not the answer</h2>
      <p>
        If you sell a handful of items or take custom orders, a simple order form or quote
        request often beats a full store. Don't pay for shopping-cart complexity your customers
        will never use. An honest designer will tell you this; a salesman won't.
      </p>

      <h2>What to do next</h2>
      <p>
        Thinking about selling online? Request a <Link to="/free-mockup">free website mockup</Link>{" "}
        and mention your products — we'll show you what your store could look like. Or read our{" "}
        <Link to="/learn/web-design-in-colorado">Colorado web design buyer's guide</Link> for the
        full picture on costs and timelines.
      </p>
    </ArticleLayout>
  );
}
